import type { InformeAggregation, InformeFilterOperator } from '../components/shared/informeDinamicoUtils';
import {
  admiteVariosValores,
  agruparFiltrosMismoCampo,
  esCampoFiltroVenta,
  guardarArticulo,
  guardarCliente,
  guardarLocalidad,
  guardarVendedor,
  leerArticulo,
  leerCliente,
  leerListaValores,
  leerLocalidad,
  leerVendedor,
} from '../components/shared/filtrosAdicionalesVentas';

export type ModuloInformePersonalizado = 'Ventas' | 'Compras' | 'Contabilidad' | 'Tesoreria';
export type TipoInformePersonalizado = 'detalle' | 'resumen';
export type OperacionInformePersonalizado = 'suma' | 'conteo' | 'promedio' | 'minimo' | 'maximo';
export type TipoGraficoInforme = 'barras' | 'lineas' | 'torta' | 'area' | 'ninguno';
export type AccesoInformePersonalizado = 'propietario' | 'editor' | 'consulta';

export interface UsuarioEmpresaOpcion {
  id: number;
  nombre: string;
  usuario: string;
}

export interface FiltroInformeApi {
  campo: string;
  operador: string;
  valor: string;
  valorHasta: string | null;
}

export interface ColumnaInformeApi {
  campo: string;
  orden: number;
}

export interface ResumenInformeApi {
  campoAgrupacion: string;
  medida: string;
  operacion: OperacionInformePersonalizado;
  tipoGrafico: TipoGraficoInforme;
}

export interface FiltrosOrigenApi {
  fechaDesde: string;
  fechaHasta: string;
  nroCliente?: number | null;
  nroProveedor?: number | null;
  codigoArticulo?: string | null;
  nroTipoFormulario: string | null;
  nroSuc: number | null;
  nroCuenta?: number | null;
  nroBanco?: number | null;
}

export interface InformePersonalizadoUpsert {
  titulo: string;
  modulo: ModuloInformePersonalizado;
  tipoInforme: TipoInformePersonalizado;
  filtrosOrigen: FiltrosOrigenApi;
  columnas: ColumnaInformeApi[];
  filtros: FiltroInformeApi[];
  resumen?: ResumenInformeApi | null;
  usuariosVer: number[] | null;
  usuariosEditar: number[] | null;
}

export interface InformePersonalizadoListItem {
  id: number;
  titulo: string;
  modulo: ModuloInformePersonalizado;
  tipo: TipoInformePersonalizado;
  fechaDesde: string | null;
  fechaHasta: string | null;
  usuariosVer: number[];
  usuariosEditar: number[];
  acceso: AccesoInformePersonalizado;
}

export interface InformePersonalizadoDetalle extends InformePersonalizadoListItem {
  filtrosOrigen: FiltrosOrigenApi;
  columnas: ColumnaInformeApi[];
  filtros: FiltroInformeApi[];
  resumen: ResumenInformeApi | null;
}

export interface ErrorValidacionInforme {
  clave: string;
  mensaje: string;
}

export interface BorradorInformePersonalizado {
  titulo: string;
  modulo: ModuloInformePersonalizado;
  tipo: TipoInformePersonalizado;
  fechaDesde: string;
  fechaHasta: string;
  entidadId: string;
  codigoArticulo: string;
  nroTipoFormulario: string;
  nroSuc: string;
  nroCuenta?: string;
  nroBanco?: string;
  columnas: string[];
  filtros: Array<{
    fieldId: string;
    operator: InformeFilterOperator;
    value: string;
    valorHasta: string;
  }>;
  campoAgrupacion: string;
  medida: string;
  agregacion: InformeAggregation;
  tipoGrafico: TipoGraficoInforme;
  usuariosVer: number[];
  usuariosEditar: number[];
  incluirUsuarios: boolean;
}

const OPERADOR_A_API: Record<InformeFilterOperator, string> = {
  contains: 'contiene',
  equals: 'igual',
  greaterThan: 'mayorIgual',
  lessThan: 'menorIgual',
  between: 'entre',
};

const OPERADOR_DESDE_API: Record<string, InformeFilterOperator> = {
  contiene: 'contains',
  igual: 'equals',
  mayorigual: 'greaterThan',
  mayoroigual: 'greaterThan',
  mayor: 'greaterThan',
  menorigual: 'lessThan',
  menoroigual: 'lessThan',
  menor: 'lessThan',
  entre: 'between',
  contains: 'contains',
  equals: 'equals',
  greaterthan: 'greaterThan',
  lessthan: 'lessThan',
  between: 'between',
};

const COLUMNA_UI_A_API: Record<string, string> = {
  fecha: 'Fecha',
  comprobante: 'NroComprobante',
  tipo: 'TipDescripcion',
  entidadNumero: 'NroCliente',
  articuloCodigo: 'CodigoArticulo',
  articulo: 'IteDescripcion',
  cantidad: 'IteCantidad',
  precioUnitario: 'PrecioUnitario',
  subtotal: 'Subtotal',
  total: 'ImporteTotal',
  sucursal: 'NroSuc',
  vendedor: 'VenNombreVendedor',
  formaPago: 'FormaDePago',
  ubicacion: 'NroUbicacion',
  ciudad: 'CiuNombreCiudad',
  provincia: 'PciaNombreProvincia',
  ramo: 'NombreRamo',
  linea: 'NombreLinea',
  clase: 'NombreClase',
  usuario: 'Usuario',
};

const COLUMNA_API_A_UI: Record<string, string> = {
  fecha: 'fecha',
  fechaemision: 'fecha',
  comprobante: 'comprobante',
  nrocomprobante: 'comprobante',
  tipo: 'tipo',
  tipdescripcion: 'tipo',
  nrotipoformulario: 'tipo',
  entidad: 'entidad',
  entidadnumero: 'entidadNumero',
  clinombre: 'entidad',
  pronombre: 'entidad',
  nombre: 'entidad',
  nrocliente: 'entidadNumero',
  articulocodigo: 'articuloCodigo',
  codigoarticulo: 'articuloCodigo',
  itecodigoarticulo: 'articuloCodigo',
  articulo: 'articulo',
  itedescripcion: 'articulo',
  descripcion: 'articulo',
  cantidad: 'cantidad',
  itecantidad: 'cantidad',
  preciounitario: 'precioUnitario',
  preciounitariofinal: 'precioUnitario',
  subtotal: 'subtotal',
  subtotalfinal: 'subtotal',
  total: 'total',
  importetotal: 'total',
  sucursal: 'sucursal',
  nrosuc: 'sucursal',
  vendedor: 'vendedor',
  vennombrevendedor: 'vendedor',
  fornrovendedor: 'vendedor',
  formapago: 'formaPago',
  formadepago: 'formaPago',
  ubicacion: 'ubicacion',
  nroubicacion: 'ubicacion',
  ciudad: 'ciudad',
  ciunombreciudad: 'ciudad',
  provincia: 'provincia',
  pcianombreprovincia: 'provincia',
  ramo: 'ramo',
  nombreramo: 'ramo',
  linea: 'linea',
  nombrelinea: 'linea',
  clase: 'clase',
  nombreclase: 'clase',
  usuario: 'usuario',
};

function claveCampo(campo: string): string {
  return campo.trim().toLowerCase();
}

function moduloUsaCamposDeVista(modulo?: ModuloInformePersonalizado): boolean {
  return modulo === 'Contabilidad' || modulo === 'Tesoreria';
}

export function campoColumnaAApi(campo: string, modulo: ModuloInformePersonalizado): string {
  if (moduloUsaCamposDeVista(modulo)) return campo.trim();
  if (claveCampo(campo) === 'entidad') return modulo === 'Ventas' ? 'CliNombre' : 'ProNombre';
  return COLUMNA_UI_A_API[campo] ?? campo;
}

export function campoColumnaDesdeApi(campo: string, modulo?: ModuloInformePersonalizado): string {
  if (moduloUsaCamposDeVista(modulo)) return campo.trim();
  return COLUMNA_API_A_UI[claveCampo(campo)] ?? campo;
}

function operadorDesdeApi(operador: string): InformeFilterOperator {
  return OPERADOR_DESDE_API[claveCampo(operador)] ?? 'equals';
}

const OPERACION_A_API: Record<InformeAggregation, OperacionInformePersonalizado> = {
  sum: 'suma',
  count: 'conteo',
  average: 'promedio',
  min: 'minimo',
  max: 'maximo',
};

const OPERACION_DESDE_API: Record<string, InformeAggregation> = {
  suma: 'sum',
  conteo: 'count',
  promedio: 'average',
  minimo: 'min',
  maximo: 'max',
  sum: 'sum',
  count: 'count',
  average: 'average',
  min: 'min',
  max: 'max',
};

export function fechaJson(value: string | null | undefined): string {
  if (!value) return '';
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match?.[1] ?? '';
}

export function fechaFiltroAApi(value: string): string {
  const iso = fechaJson(value);
  if (iso) {
    const [year, month, day] = iso.split('-');
    return `${day}-${month}-${year}`;
  }
  const local = String(value).trim().match(/^(\d{2})-(\d{2})-(\d{4})$/);
  return local ? `${local[1]}-${local[2]}-${local[3]}` : String(value).trim();
}

export function fechaFiltroDesdeApi(value: string | null | undefined): string {
  if (!value) return '';
  const local = String(value).trim().match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (local) return `${local[3]}-${local[2]}-${local[1]}`;
  return fechaJson(value) || String(value).trim();
}

function enteroONull(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function textoONull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed || null;
}

function idsNumericos(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => Number(item))
    .filter((item) => Number.isInteger(item) && item > 0);
}

function normalizarClaveError(clave: string): string {
  return clave
    .split('.')
    .map((part) => part.replace(/^([^[\]]+)/, (name) => name.charAt(0).toLowerCase() + name.slice(1)))
    .join('.');
}

export function claveErrorNormalizada(clave: string): string {
  return normalizarClaveError(clave);
}

export function leerErroresValidacion(error: unknown): ErrorValidacionInforme[] {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (!data || typeof data !== 'object') return [];
  const errors = (data as { errors?: unknown }).errors;
  if (!errors || typeof errors !== 'object') return [];
  return Object.entries(errors as Record<string, unknown>).flatMap(([clave, value]) => {
    const mensajes = (Array.isArray(value) ? value : [value])
      .filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
    return mensajes.length ? [{ clave, mensaje: mensajes.join(' ') }] : [];
  });
}

export function mensajeDeClave(errores: ErrorValidacionInforme[], clave: string): string | undefined {
  const buscada = normalizarClaveError(clave);
  return errores.find((error) => normalizarClaveError(error.clave) === buscada)?.mensaje;
}

function leerCampo(raw: Record<string, unknown>, nombre: string): unknown {
  if (raw[nombre] !== undefined) return raw[nombre];
  const pascal = nombre.charAt(0).toUpperCase() + nombre.slice(1);
  return raw[pascal];
}

export function resolverAccesoInforme(raw: Record<string, unknown>): AccesoInformePersonalizado {
  const accionesRaw = leerCampo(raw, 'acciones');
  const acciones = Array.isArray(accionesRaw)
    ? accionesRaw.map((item) => String(item).toLowerCase())
    : [];
  if (acciones.includes('eliminar') || acciones.includes('compartir')) return 'propietario';
  const marca = leerCampo(raw, 'esPropietario') ?? leerCampo(raw, 'esDueno') ?? leerCampo(raw, 'propietario');
  if (marca === true) return 'propietario';
  const rol = String(leerCampo(raw, 'rol') ?? leerCampo(raw, 'acceso') ?? leerCampo(raw, 'permiso') ?? '').trim().toLowerCase();
  if (rol === 'propietario' || rol === 'dueno' || rol === 'dueño' || rol === 'owner') return 'propietario';
  if (rol === 'consulta' || rol === 'ver' || rol === 'lector' || rol === 'lectura' || rol === 'viewer') return 'consulta';
  if (rol === 'editor' || rol === 'editar') return 'editor';
  if (leerCampo(raw, 'soloLectura') === true || leerCampo(raw, 'puedeEditar') === false) return 'consulta';
  if (leerCampo(raw, 'puedeCompartir') === true || leerCampo(raw, 'puedeEliminar') === true) return 'propietario';
  if (acciones.includes('editar')) return 'editor';
  if (acciones.includes('ver') || acciones.includes('consultar')) return 'consulta';
  if (marca === false) return 'editor';
  return 'editor';
}

function leerFiltrosOrigen(raw: Record<string, unknown>, modulo: ModuloInformePersonalizado): FiltrosOrigenApi {
  const origen = (raw.filtrosOrigen ?? raw.FiltrosOrigen ?? raw) as Record<string, unknown>;
  const nroCliente = origen.nroCliente ?? origen.NroCliente;
  const nroProveedor = origen.nroProveedor ?? origen.NroProveedor;
  const nroSuc = origen.nroSuc ?? origen.NroSuc;
  const nroCuenta = origen.nroCuenta ?? origen.NroCuenta;
  const nroBanco = origen.nroBanco ?? origen.NroBanco;
  const entidad = modulo === 'Compras'
    ? { nroProveedor: nroProveedor == null || nroProveedor === '' ? null : Number(nroProveedor) }
    : modulo === 'Tesoreria'
      ? {}
      : { nroCliente: nroCliente == null || nroCliente === '' ? null : Number(nroCliente) };
  return {
    fechaDesde: fechaJson(String(origen.fechaDesde ?? origen.FechaDesde ?? raw.fechaDesde ?? '')),
    fechaHasta: fechaJson(String(origen.fechaHasta ?? origen.FechaHasta ?? raw.fechaHasta ?? '')),
    ...entidad,
    ...(modulo === 'Contabilidad'
      ? { nroCuenta: nroCuenta == null || nroCuenta === '' ? null : Number(nroCuenta) }
      : {}),
    ...(modulo === 'Tesoreria'
      ? { nroBanco: nroBanco == null || nroBanco === '' ? null : Number(nroBanco) }
      : {}),
    codigoArticulo: moduloUsaCamposDeVista(modulo) ? null : textoONull(String(origen.codigoArticulo ?? origen.CodigoArticulo ?? '')),
    nroTipoFormulario: textoONull(String(origen.nroTipoFormulario ?? origen.NroTipoFormulario ?? '')),
    nroSuc: nroSuc == null || nroSuc === '' ? null : Number(nroSuc),
  };
}

function leerColumnas(raw: Record<string, unknown>): ColumnaInformeApi[] {
  const columnas = raw.columnas ?? raw.Columnas;
  if (!Array.isArray(columnas)) return [];
  return columnas.map((item, index) => {
    const columna = (item ?? {}) as Record<string, unknown>;
    const orden = Number(columna.orden ?? columna.Orden);
    return {
      campo: String(columna.campo ?? columna.Campo ?? ''),
      orden: Number.isFinite(orden) ? orden : index + 1,
    };
  }).filter((columna) => columna.campo);
}

function leerFiltros(raw: Record<string, unknown>): FiltroInformeApi[] {
  const filtros = raw.filtros ?? raw.Filtros;
  if (!Array.isArray(filtros)) return [];
  return filtros.map((item) => {
    const filtro = (item ?? {}) as Record<string, unknown>;
    const valorHasta = filtro.valorHasta ?? filtro.ValorHasta;
    return {
      campo: String(filtro.campo ?? filtro.Campo ?? ''),
      operador: String(filtro.operador ?? filtro.Operador ?? 'igual'),
      valor: String(filtro.valor ?? filtro.Valor ?? ''),
      valorHasta: valorHasta == null || valorHasta === '' ? null : String(valorHasta),
    };
  }).filter((filtro) => filtro.campo);
}

function leerResumen(raw: Record<string, unknown>, tipo: TipoInformePersonalizado): ResumenInformeApi | null {
  if (tipo !== 'resumen') return null;
  const resumen = (raw.resumen ?? raw.Resumen) as Record<string, unknown> | null | undefined;
  if (!resumen || typeof resumen !== 'object') return null;
  const operacion = String(resumen.operacion ?? resumen.Operacion ?? 'suma');
  const tipoGrafico = String(resumen.tipoGrafico ?? resumen.TipoGrafico ?? 'ninguno');
  const grafico = (['barras', 'lineas', 'torta', 'area', 'ninguno'].includes(tipoGrafico)
    ? tipoGrafico
    : tipoGrafico === 'circular' ? 'torta' : 'ninguno') as TipoGraficoInforme;
  return {
    campoAgrupacion: String(resumen.campoAgrupacion ?? resumen.CampoAgrupacion ?? ''),
    medida: String(resumen.medida ?? resumen.Medida ?? ''),
    operacion: (OPERACION_DESDE_API[operacion] ? OPERACION_A_API[OPERACION_DESDE_API[operacion]] : 'suma'),
    tipoGrafico: grafico,
  };
}

function leerTipo(raw: Record<string, unknown>): TipoInformePersonalizado {
  const tipo = String(raw.tipoInforme ?? raw.TipoInforme ?? raw.tipo ?? raw.Tipo ?? 'detalle').toLowerCase();
  return tipo === 'resumen' || tipo === 'grafico' ? 'resumen' : 'detalle';
}

function leerModulo(raw: Record<string, unknown>, fallback: ModuloInformePersonalizado): ModuloInformePersonalizado {
  const modulo = String(raw.modulo ?? raw.Modulo ?? fallback);
  if (modulo === 'Compras' || modulo === 'Contabilidad' || modulo === 'Ventas' || modulo === 'Tesoreria') return modulo;
  return fallback;
}

export function normalizarListItem(
  raw: Record<string, unknown>,
  moduloConsulta: ModuloInformePersonalizado,
): InformePersonalizadoListItem {
  const modulo = leerModulo(raw, moduloConsulta);
  const origen = leerFiltrosOrigen(raw, modulo);
  return {
    id: Number(raw.id ?? raw.Id),
    titulo: String(raw.titulo ?? raw.Titulo ?? ''),
    modulo,
    tipo: leerTipo(raw),
    fechaDesde: origen.fechaDesde || null,
    fechaHasta: origen.fechaHasta || null,
    usuariosVer: idsNumericos(raw.usuariosVer ?? raw.UsuariosVer),
    usuariosEditar: idsNumericos(raw.usuariosEditar ?? raw.UsuariosEditar),
    acceso: resolverAccesoInforme(raw),
  };
}

export function normalizarDetalle(
  raw: Record<string, unknown>,
  moduloConsulta: ModuloInformePersonalizado,
): InformePersonalizadoDetalle {
  const base = normalizarListItem(raw, moduloConsulta);
  return {
    ...base,
    filtrosOrigen: leerFiltrosOrigen(raw, base.modulo),
    columnas: leerColumnas(raw),
    filtros: leerFiltros(raw),
    resumen: leerResumen(raw, base.tipo),
  };
}

function filtroAApi(
  filtro: BorradorInformePersonalizado['filtros'][number],
  modulo: ModuloInformePersonalizado,
  esFecha: boolean,
): FiltroInformeApi {
  const operador = OPERADOR_A_API[filtro.operator];
  const valorHasta = filtro.operator === 'between'
    ? (esFecha ? fechaFiltroAApi(filtro.valorHasta) : filtro.valorHasta || null)
    : null;
  if (moduloUsaCamposDeVista(modulo)) {
    return {
      campo: campoColumnaAApi(filtro.fieldId, modulo),
      operador,
      valor: esFecha ? fechaFiltroAApi(filtro.value) : filtro.value,
      valorHasta,
    };
  }
  const esProveedor = filtro.fieldId === 'proveedor' || (modulo === 'Compras' && filtro.fieldId === 'cliente');
  if (filtro.fieldId === 'cliente' || esProveedor) {
    const entidad = leerCliente(filtro.value);
    if (esProveedor) {
      if (entidad.nombre) {
        return {
          campo: 'ProNombre',
          operador: filtro.operator === 'contains' ? 'contiene' : 'igual',
          valor: entidad.nombre,
          valorHasta: null,
        };
      }
      return { campo: 'NroCliente', operador: 'igual', valor: entidad.nro, valorHasta: null };
    }
    if (entidad.nro) {
      return { campo: 'NroCliente', operador: 'igual', valor: entidad.nro, valorHasta: null };
    }
    return {
      campo: 'CliNombre',
      operador: filtro.operator === 'contains' ? 'contiene' : 'igual',
      valor: entidad.nombre,
      valorHasta: null,
    };
  }
  if (filtro.fieldId === 'articulo') {
    const articulo = leerArticulo(filtro.value);
    if (articulo.codigo) {
      return { campo: 'CodigoArticulo', operador: 'igual', valor: articulo.codigo, valorHasta: null };
    }
    return {
      campo: 'IteDescripcion',
      operador: filtro.operator === 'contains' ? 'contiene' : 'igual',
      valor: articulo.descripcion,
      valorHasta: null,
    };
  }
  if (filtro.fieldId === 'localidad') {
    const localidad = leerLocalidad(filtro.value);
    if (localidad.ciudad) {
      return { campo: 'CiuNombreCiudad', operador: 'igual', valor: localidad.ciudad, valorHasta: null };
    }
    return { campo: 'PciaNombreProvincia', operador: 'igual', valor: localidad.provincia, valorHasta: null };
  }
  if (filtro.fieldId === 'vendedor') {
    const vendedor = leerVendedor(filtro.value);
    if (vendedor.nro) {
      return { campo: 'ForNroVendedor', operador: 'igual', valor: vendedor.nro, valorHasta: null };
    }
    return {
      campo: 'VenNombreVendedor',
      operador: filtro.operator === 'contains' ? 'contiene' : 'igual',
      valor: vendedor.nombre,
      valorHasta: null,
    };
  }
  if (filtro.fieldId === 'tipo') {
    return { campo: 'NroTipoFormulario', operador: 'igual', valor: filtro.value.trim(), valorHasta: null };
  }
  return {
    campo: campoColumnaAApi(filtro.fieldId, modulo),
    operador,
    valor: esFecha ? fechaFiltroAApi(filtro.value) : filtro.value,
    valorHasta,
  };
}

function filtroUiDesdeApi(
  campo: string,
  valor: string,
  operator: InformeFilterOperator,
  modulo: ModuloInformePersonalizado,
): { fieldId: string; operator: InformeFilterOperator; value: string } {
  if (moduloUsaCamposDeVista(modulo)) {
    return { fieldId: campo.trim(), operator, value: valor };
  }
  const key = claveCampo(campo);
  if (modulo === 'Ventas' && key === 'nrocliente') {
    return { fieldId: 'cliente', operator: 'equals', value: guardarCliente({ nro: valor, nombre: '' }) };
  }
  if (modulo === 'Ventas' && (key === 'clinombre' || key === 'nombre')) {
    return {
      fieldId: 'cliente',
      operator: operator === 'contains' ? 'contains' : 'equals',
      value: guardarCliente({ nro: '', nombre: valor }),
    };
  }
  if (modulo === 'Compras' && (key === 'nrocliente' || key === 'nroproveedor')) {
    return { fieldId: 'proveedor', operator: 'equals', value: guardarCliente({ nro: valor, nombre: '' }) };
  }
  if (modulo === 'Compras' && (key === 'pronombre' || key === 'nombre')) {
    return {
      fieldId: 'proveedor',
      operator: operator === 'contains' ? 'contains' : 'equals',
      value: guardarCliente({ nro: '', nombre: valor }),
    };
  }
  if (key === 'codigoarticulo' || key === 'itecodigoarticulo') {
    return { fieldId: 'articulo', operator: 'equals', value: guardarArticulo({ codigo: valor, descripcion: '' }) };
  }
  if (key === 'itedescripcion' || key === 'descripcion') {
    return {
      fieldId: 'articulo',
      operator: operator === 'contains' ? 'contains' : 'equals',
      value: guardarArticulo({ codigo: '', descripcion: valor }),
    };
  }
  if (key === 'ciunombreciudad') {
    return { fieldId: 'localidad', operator: 'equals', value: guardarLocalidad({ ciudad: valor, provincia: '' }) };
  }
  if (key === 'pcianombreprovincia') {
    return { fieldId: 'localidad', operator: 'equals', value: guardarLocalidad({ ciudad: '', provincia: valor }) };
  }
  if (key === 'fornrovendedor') {
    return { fieldId: 'vendedor', operator: 'equals', value: guardarVendedor({ nro: valor, nombre: '' }) };
  }
  if (key === 'vennombrevendedor') {
    return {
      fieldId: 'vendedor',
      operator: operator === 'contains' ? 'contains' : 'equals',
      value: guardarVendedor({ nro: '', nombre: valor }),
    };
  }
  if (key === 'nrotipoformulario') {
    return { fieldId: 'tipo', operator: 'equals', value: valor };
  }
  return { fieldId: campoColumnaDesdeApi(campo), operator, value: valor };
}

export function armarPayloadInforme(borrador: BorradorInformePersonalizado, camposFecha: string[]): InformePersonalizadoUpsert {
  const entidad = enteroONull(borrador.entidadId);
  const filtrosOrigen: FiltrosOrigenApi = {
    fechaDesde: fechaJson(borrador.fechaDesde),
    fechaHasta: fechaJson(borrador.fechaHasta),
    nroTipoFormulario: textoONull(borrador.nroTipoFormulario),
    nroSuc: enteroONull(borrador.nroSuc),
  };
  if (borrador.modulo === 'Compras') {
    filtrosOrigen.nroProveedor = entidad;
    filtrosOrigen.codigoArticulo = textoONull(borrador.codigoArticulo);
  } else if (borrador.modulo === 'Contabilidad') {
    filtrosOrigen.nroCliente = entidad;
    filtrosOrigen.nroCuenta = enteroONull(borrador.nroCuenta ?? '');
  } else if (borrador.modulo === 'Tesoreria') {
    filtrosOrigen.nroBanco = enteroONull(borrador.nroBanco ?? '');
  } else {
    filtrosOrigen.nroCliente = entidad;
    filtrosOrigen.codigoArticulo = textoONull(borrador.codigoArticulo);
  }
  const fechas = new Set(camposFecha);
  const payload: InformePersonalizadoUpsert = {
    titulo: borrador.titulo.trim(),
    modulo: borrador.modulo,
    tipoInforme: borrador.tipo,
    filtrosOrigen,
    columnas: borrador.columnas.map((campo, index) => ({
      campo: campoColumnaAApi(campo, borrador.modulo),
      orden: index + 1,
    })),
    filtros: borrador.filtros.flatMap((filtro) => {
      const varios = admiteVariosValores(filtro.operator) && !esCampoFiltroVenta(filtro.fieldId)
        ? leerListaValores(filtro.value)
        : [];
      const valores = varios.length > 0 ? varios : [filtro.value];
      return valores.map((valor) => filtroAApi(
        { ...filtro, value: valor },
        borrador.modulo,
        fechas.has(filtro.fieldId),
      ));
    }),
    usuariosVer: borrador.incluirUsuarios ? borrador.usuariosVer : null,
    usuariosEditar: borrador.incluirUsuarios ? borrador.usuariosEditar : null,
  };
  if (borrador.tipo === 'resumen') {
    payload.resumen = {
      campoAgrupacion: campoColumnaAApi(borrador.campoAgrupacion, borrador.modulo),
      medida: campoColumnaAApi(borrador.medida, borrador.modulo),
      operacion: OPERACION_A_API[borrador.agregacion],
      tipoGrafico: borrador.tipoGrafico,
    };
  }
  return payload;
}

export function estadoDesdeDetalle(
  detalle: InformePersonalizadoDetalle,
  catalogoCampos: string[],
  camposFecha: string[] = [],
): BorradorInformePersonalizado {
  const columnasOrdenadas = [...detalle.columnas].sort((left, right) => {
    const leftOrden = Number.isFinite(left.orden) ? left.orden : Number.MAX_SAFE_INTEGER;
    const rightOrden = Number.isFinite(right.orden) ? right.orden : Number.MAX_SAFE_INTEGER;
    return leftOrden - rightOrden;
  });
  const seleccionadas = columnasOrdenadas
    .map((columna) => campoColumnaDesdeApi(columna.campo, detalle.modulo))
    .filter((campo) => catalogoCampos.includes(campo));
  const entidad = detalle.modulo === 'Compras'
    ? detalle.filtrosOrigen.nroProveedor
    : detalle.modulo === 'Tesoreria'
      ? null
      : detalle.filtrosOrigen.nroCliente;
  const resumen = detalle.resumen;
  const agrupacionDefault = detalle.modulo === 'Contabilidad'
    ? 'NombreCuenta'
    : detalle.modulo === 'Tesoreria'
      ? 'NombreBanco'
      : 'entidad';
  const medidaDefault = detalle.modulo === 'Contabilidad' || detalle.modulo === 'Tesoreria' ? 'Debe' : 'subtotal';
  return {
    titulo: detalle.titulo,
    modulo: detalle.modulo,
    tipo: detalle.tipo,
    fechaDesde: detalle.filtrosOrigen.fechaDesde,
    fechaHasta: detalle.filtrosOrigen.fechaHasta,
    entidadId: entidad ? String(entidad) : '',
    codigoArticulo: detalle.filtrosOrigen.codigoArticulo ?? '',
    nroTipoFormulario: detalle.filtrosOrigen.nroTipoFormulario ?? '',
    nroSuc: detalle.filtrosOrigen.nroSuc ? String(detalle.filtrosOrigen.nroSuc) : '',
    nroCuenta: detalle.filtrosOrigen.nroCuenta ? String(detalle.filtrosOrigen.nroCuenta) : '',
    nroBanco: detalle.filtrosOrigen.nroBanco ? String(detalle.filtrosOrigen.nroBanco) : '',
    columnas: seleccionadas,
    filtros: agruparFiltrosMismoCampo(detalle.filtros.map((filtro) => {
      const operator = operadorDesdeApi(filtro.operador);
      const traducido = filtroUiDesdeApi(filtro.campo, filtro.valor, operator, detalle.modulo);
      const esFecha = camposFecha.includes(traducido.fieldId);
      return {
        fieldId: traducido.fieldId,
        operator: traducido.operator,
        value: esFecha ? fechaFiltroDesdeApi(traducido.value) : traducido.value,
        valorHasta: esFecha ? fechaFiltroDesdeApi(filtro.valorHasta) : (filtro.valorHasta ?? ''),
      };
    })),
    campoAgrupacion: resumen?.campoAgrupacion ? campoColumnaDesdeApi(resumen.campoAgrupacion, detalle.modulo) : agrupacionDefault,
    medida: resumen?.medida ? campoColumnaDesdeApi(resumen.medida, detalle.modulo) : medidaDefault,
    agregacion: resumen ? (OPERACION_DESDE_API[resumen.operacion] ?? 'sum') : 'sum',
    tipoGrafico: resumen?.tipoGrafico ?? 'ninguno',
    usuariosVer: detalle.usuariosVer,
    usuariosEditar: detalle.usuariosEditar,
    incluirUsuarios: detalle.acceso === 'propietario',
  };
}

export function statusHttp(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}
